import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, StatCard } from "../components/PageHeader";
import api, { formatApiError, formatINR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Check, X, Pencil, Trash, Receipt, Upload, ArrowClockwise } from "@phosphor-icons/react";

const STATUS_STYLES = {
  pending: { bg: "#FEF3C7", fg: "#92400E", label: "Pending" },
  approved: { bg: "#D1FAE5", fg: "#065F46", label: "Approved" },
  rejected: { bg: "#FEE2E2", fg: "#991B1B", label: "Rejected" },
  modified: { bg: "#DBEAFE", fg: "#1E40AF", label: "Modified" },
};

const EMPTY = { date: new Date().toISOString().slice(0, 10), category: "", amount: "", description: "" };
const EMPTY_LINE = { category: "", amount: "", description: "" };

export default function Expenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lines, setLines] = useState([]); // multi-row day claim (bulk mode)
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => {
    const [{ data: exps }, { data: st }] = await Promise.all([
      api.get("/expenses", { params: filterStatus ? { status: filterStatus } : {} }),
      api.get("/settings"),
    ]);
    setItems(exps);
    setCategories((st.transaction_category || []).map((c) => c.value));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterStatus]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setReceiptFile(null); setShowForm(true); };
  const openEdit = (x) => { setEditing(x); setForm({ date: x.date, category: x.category, amount: x.amount, description: x.description }); setReceiptFile(null); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const amount = parseFloat(form.amount);
      if (!(amount > 0)) throw new Error("Amount must be greater than 0");
      let id;
      if (editing) {
        await api.put(`/expenses/${editing.id}`, { ...form, amount });
        id = editing.id;
      } else {
        const { data: created } = await api.post("/expenses", { ...form, amount });
        id = created.id;
      }
      if (receiptFile) {
        const fd = new FormData(); fd.append("file", receiptFile);
        await api.post(`/expenses/${id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing ? "Expense updated" : "Expense submitted");
      setShowForm(false); setForm(EMPTY); setReceiptFile(null);
      load();
    } catch (err) { toast.error(err.response ? formatApiError(err.response?.data?.detail) : err.message); }
    finally { setSubmitting(false); }
  };

  const approve = async (x) => {
    const newAmt = window.prompt(`Approve ${x.employee_name}'s expense.\nEnter final approved amount in ₹ (blank to keep ${x.amount}):`, "");
    if (newAmt === null) return;
    const notes = window.prompt("Any note for the employee? (optional)", "") || "";
    try {
      await api.post(`/expenses/${x.id}/approve`, { review_notes: notes, amount: newAmt ? parseFloat(newAmt) : null });
      toast.success("Expense approved"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reject = async (x) => {
    const notes = window.prompt("Reason for rejecting (optional):", ""); if (notes === null) return;
    try {
      await api.post(`/expenses/${x.id}/reject`, { review_notes: notes });
      toast.success("Expense rejected"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const modify = async (x) => {
    const newAmt = window.prompt(`Modify amount for ${x.employee_name}. Current ₹${x.amount}:`, x.amount);
    if (newAmt === null) return;
    const amt = parseFloat(newAmt); if (!(amt > 0)) { toast.error("Invalid amount"); return; }
    const notes = window.prompt("Reason for modification (optional):", "") || "";
    try {
      await api.put(`/expenses/${x.id}`, { amount: amt, review_notes: notes });
      toast.success("Expense modified"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resetPending = async (x) => {
    try {
      await api.post(`/expenses/${x.id}/reset-pending`);
      toast.success("Reset to pending"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (x) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${x.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const viewReceipt = async (x) => {
    try {
      const res = await api.get(`/expenses/${x.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch { toast.error("Could not open receipt"); }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    const valid = lines
      .map((l) => ({ ...l, amount: parseFloat(l.amount) }))
      .filter((l) => l.category && l.amount > 0);
    if (valid.length === 0) { toast.error("Add at least one expense line with category + amount"); return; }
    setSubmitting(true);
    try {
      for (const l of valid) {
        await api.post("/expenses", { date: bulkDate, category: l.category, amount: l.amount, description: l.description || "" });
      }
      toast.success(`Submitted ${valid.length} expense${valid.length > 1 ? "s" : ""} for ${bulkDate}`);
      setShowBulk(false);
      setLines([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, modified: 0, total: 0 };
    for (const i of items) {
      c[i.status] = (c[i.status] || 0) + 1;
      if (i.status === "approved") c.total += Number(i.amount || 0);
    }
    return c;
  }, [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Reimbursements"
        title="Expense Claims"
        subtitle={isAdmin ? "Approve, reject, or modify employee expenses. Receipts open in a new tab." : "Submit daily business expenses with receipts and track their approval."}
        actions={
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => { setShowBulk(true); setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]); }} data-testid="new-bulk-expense-btn"
                    className="brand-btn px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              <Plus size={16} weight="bold" /> Multi-row Day Claim
            </button>
            <button onClick={openNew} data-testid="new-expense-btn"
                    className="border border-[#106109] text-[#106109] px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#F0F7EF]">
              <Plus size={16} weight="bold" /> Single Expense
            </button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Pending" value={counts.pending} testId="stat-exp-pending" />
          <StatCard label="Approved" value={counts.approved} hint={formatINR(counts.total)} testId="stat-exp-approved" />
          <StatCard label="Modified" value={counts.modified} testId="stat-exp-modified" />
          <StatCard label="Rejected" value={counts.rejected} testId="stat-exp-rejected" />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <label className="label-caps mr-1">Status</label>
          <div className="flex border border-[#E5E7EB]">
            {[{ v: "", l: "All" }, { v: "pending", l: "Pending" }, { v: "approved", l: "Approved" }, { v: "modified", l: "Modified" }, { v: "rejected", l: "Rejected" }].map((o) => (
              <button key={o.v} onClick={() => setFilterStatus(o.v)}
                      data-testid={`exp-filter-${o.v || "all"}`}
                      className={`px-3 py-2 text-sm border-r border-[#E5E7EB] last:border-r-0 transition-colors duration-200 ${filterStatus === o.v ? "bg-[#106109] text-white" : "hover:bg-[#F9FAFB]"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left">
                {isAdmin
                  ? ["Date", "Employee", "Category", "Amount", "Description", "Receipt", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)
                  : ["Date", "Category", "Amount", "Description", "Receipt", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)
                }
              </tr>
            </thead>
            <tbody>
              {items.map((x) => {
                const s = STATUS_STYLES[x.status] || STATUS_STYLES.pending;
                return (
                  <tr key={x.id} className="border-b border-[#F3F4F6]" data-testid={`exp-row-${x.id}`}>
                    <td className="px-4 py-3 font-mono">{x.date}</td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="font-medium">{x.employee_name}</div>
                        <div className="text-xs text-[#6B7280]">{x.employee_code} · {x.department}</div>
                      </td>
                    )}
                    <td className="px-4 py-3">{x.category}</td>
                    <td className="px-4 py-3 font-mono font-semibold">{formatINR(x.amount)}
                      {x.original_amount && Number(x.original_amount) !== Number(x.amount) && (
                        <div className="text-[10px] text-[#6B7280] line-through">was {formatINR(x.original_amount)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] max-w-xs truncate" title={x.description}>{x.description || "—"}</td>
                    <td className="px-4 py-3">
                      {x.receipt_path ? (
                        <button onClick={() => viewReceipt(x)} className="text-xs text-[#106109] hover:underline inline-flex items-center gap-1" data-testid={`view-receipt-${x.id}`}>
                          <Receipt size={14} /> View
                        </button>
                      ) : <span className="text-xs text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold" style={{ background: s.bg, color: s.fg }} data-testid={`exp-status-${x.id}`}>{s.label}</span>
                      {x.reviewed_by && <div className="text-[10px] text-[#6B7280] mt-1">by {x.reviewed_by}</div>}
                      {x.review_notes && <div className="text-[10px] text-[#6B7280] mt-0.5 italic">"{x.review_notes}"</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {isAdmin ? (
                          <>
                            {x.status !== "approved" && (
                              <button onClick={() => approve(x)} data-testid={`exp-approve-${x.id}`} title="Approve"
                                      className="border border-[#10B981] text-[#10B981] p-1.5 hover:bg-[#ECFDF5]"><Check size={14} weight="bold" /></button>
                            )}
                            {x.status !== "rejected" && (
                              <button onClick={() => reject(x)} data-testid={`exp-reject-${x.id}`} title="Reject"
                                      className="border border-[#DC2626] text-[#DC2626] p-1.5 hover:bg-[#FEF2F2]"><X size={14} weight="bold" /></button>
                            )}
                            <button onClick={() => modify(x)} data-testid={`exp-modify-${x.id}`} title="Modify amount"
                                    className="border border-[#1E40AF] text-[#1E40AF] p-1.5 hover:bg-[#EFF6FF]"><Pencil size={14} /></button>
                            {x.status !== "pending" && (
                              <button onClick={() => resetPending(x)} data-testid={`exp-pending-${x.id}`} title="Set as pending"
                                      className="border border-[#FBBF24] text-[#92400E] p-1.5 hover:bg-[#FEF3C7]"><ArrowClockwise size={14} /></button>
                            )}
                            <button onClick={() => del(x)} data-testid={`exp-delete-${x.id}`} title="Delete"
                                    className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF2F2] text-[#DC2626]"><Trash size={14} /></button>
                          </>
                        ) : (
                          <>
                            {x.status === "pending" && (
                              <button onClick={() => openEdit(x)} data-testid={`exp-edit-${x.id}`} title="Edit"
                                      className="border border-[#E5E7EB] p-1.5 hover:bg-[#F9FAFB]"><Pencil size={14} /></button>
                            )}
                            {x.status === "pending" && (
                              <button onClick={() => del(x)} data-testid={`exp-delete-${x.id}`} title="Delete"
                                      className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF2F2] text-[#DC2626]"><Trash size={14} /></button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-16 text-center text-[#9CA3AF]">No expenses yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="exp-form-modal">
          <div className="bg-white w-full max-w-lg border border-[#E5E7EB] max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#E5E7EB] px-6 py-4">
              <div className="label-caps">{editing ? "Edit" : "Submit"}</div>
              <h3 className="font-display font-bold text-xl">Expense Claim</h3>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-caps block mb-1.5">Date</label>
                  <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="exp-form-date" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Amount (₹)</label>
                  <input type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="exp-form-amount" />
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Category</label>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="exp-form-category">
                  <option value="">Select…</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="exp-form-desc" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">Receipt (optional, JPG/PNG/PDF, max 8MB)</label>
                <label className="border border-dashed border-[#E5E7EB] px-3 py-3 flex items-center justify-between cursor-pointer hover:bg-[#F9FAFB]">
                  <div className="flex items-center gap-2 text-sm">
                    <Upload size={16} />
                    <span>{receiptFile ? receiptFile.name : "Choose a file"}</span>
                  </div>
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="exp-form-receipt"
                         onChange={(e) => setReceiptFile(e.target.files[0] || null)} />
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="exp-form-submit"
                        className="brand-btn flex-1 py-2.5 text-sm font-semibold disabled:opacity-60">
                  {submitting ? "Saving…" : (editing ? "Save changes" : "Submit Expense")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showBulk && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-exp-modal">
          <div className="bg-white w-full max-w-3xl border border-[#E5E7EB] max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#E5E7EB] px-6 py-4 flex items-start justify-between">
              <div>
                <div className="label-caps">Submit</div>
                <h3 className="font-display font-bold text-xl">Full-day Expense Claim</h3>
                <div className="text-xs text-[#6B7280] mt-1">Log every business expense from one day in a single submission.</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="text-[#6B7280]">✕</button>
            </div>
            <form onSubmit={submitBulk} className="p-6 space-y-4">
              <div className="max-w-xs">
                <label className="label-caps block mb-1.5">Date</label>
                <input type="date" required value={bulkDate} onChange={(e) => setBulkDate(e.target.value)}
                       data-testid="bulk-exp-date" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
              </div>
              <div className="overflow-x-auto border border-[#F3F4F6]">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB]">
                    <tr>
                      <th className="px-2 py-2 label-caps text-[10px] text-left w-8">#</th>
                      <th className="px-2 py-2 label-caps text-[10px] text-left" style={{ minWidth: 150 }}>Category</th>
                      <th className="px-2 py-2 label-caps text-[10px] text-left" style={{ minWidth: 110 }}>Amount (₹)</th>
                      <th className="px-2 py-2 label-caps text-[10px] text-left" style={{ minWidth: 240 }}>Description</th>
                      <th className="px-2 py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} className="border-t border-[#F3F4F6]" data-testid={`bulk-exp-row-${i}`}>
                        <td className="px-2 py-1 text-[#6B7280] font-mono">{i + 1}</td>
                        <td className="px-1 py-1">
                          <select value={l.category} onChange={(e) => setLines(lines.map((x, k) => k === i ? { ...x, category: e.target.value } : x))}
                                  data-testid={`bulk-exp-${i}-category`}
                                  className="w-full border border-transparent hover:border-[#E5E7EB] focus:border-[#106109] px-2 py-1.5 text-xs focus:outline-none">
                            <option value="">Select…</option>
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" min="0" step="0.01" value={l.amount}
                                 onChange={(e) => setLines(lines.map((x, k) => k === i ? { ...x, amount: e.target.value } : x))}
                                 data-testid={`bulk-exp-${i}-amount`}
                                 className="w-full border border-transparent hover:border-[#E5E7EB] focus:border-[#106109] px-2 py-1.5 text-xs focus:outline-none" />
                        </td>
                        <td className="px-1 py-1">
                          <input value={l.description} placeholder="Optional notes"
                                 onChange={(e) => setLines(lines.map((x, k) => k === i ? { ...x, description: e.target.value } : x))}
                                 data-testid={`bulk-exp-${i}-desc`}
                                 className="w-full border border-transparent hover:border-[#E5E7EB] focus:border-[#106109] px-2 py-1.5 text-xs focus:outline-none" />
                        </td>
                        <td className="px-1 py-1 text-right">
                          <button type="button" onClick={() => setLines(lines.filter((_, k) => k !== i))}
                                  data-testid={`bulk-exp-remove-${i}`}
                                  className="p-1.5 text-[#DC2626] hover:bg-[#FEF2F2]" title="Remove row">
                            <Trash size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={() => setLines([...lines, { ...EMPTY_LINE }])}
                      data-testid="bulk-exp-add-row"
                      className="text-xs px-3 py-1.5 border border-[#106109] text-[#106109] hover:bg-[#F0F7EF] inline-flex items-center gap-1.5 font-semibold">
                <Plus size={12} weight="bold" /> Add line
              </button>
              <div className="text-xs text-[#6B7280] bg-[#F9FAFB] p-3 border border-[#E5E7EB]">
                Each row will be submitted as its own claim so the admin can approve, reject or modify them individually. Attach receipts later by editing each row.
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowBulk(false)} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="bulk-exp-submit"
                        className="brand-btn flex-1 py-2.5 text-sm font-semibold disabled:opacity-60">
                  {submitting ? "Submitting…" : `Submit ${lines.filter((l) => l.category && parseFloat(l.amount) > 0).length || 0} line(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
